"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Collapse,
  IconButton,
  CircularProgress,
  Button,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { Close, ChevronLeft } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
// Import custom hooks
import { useAccountId } from "../hooks/useAccountId";
import { useMigrationForm } from "../hooks/useMigrationForm";
import { useMigrationProcess } from "../hooks/useMigrationProcess";
import { useFileDownload } from "../hooks/useFileDownload";
// Import components
import { SetupForm } from "../components/specific/MigratiionSetup/SetupForm";
import Summary from "../components/specific/MigratiionSetup/Summary/Summary";
import { AssessmentStep } from "../components/specific/MigrationTool/Assessment/AssessmentStep";
import AdminSidebar from "../components/specific/AdminSidebar/AdminSidebar";
import { useMigrateGenerate } from "../hooks/useMigrationGenerate";
import { useAuth } from "../context/AuthContext";

// Actualizamos los pasos para incluir Assessment
// Definimos los pasos base
const baseSteps = ["Setup", "Assessment", "Migrate", "Summary"];

const MigrateAPIs = () => {
  // Use accountId hook
  const accountId = useAccountId();
  const [accountIdLoading, setAccountIdLoading] = useState(!accountId);
  const [chargingServices, setChargingServices] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [labelCharging, setLabelCharging] = useState("");
  const [migrateResult, setMigrate] = useState({});
  const [disableContinue, setDisableContinue] = useState(false);
  const [labelToast, setLabelToast] = useState("");

  const { logout } = useAuth();

  // Update accountId loading state
  useEffect(() => {
    if (accountId) {
      setAccountIdLoading(false);
    }
  }, [accountId]);

  // Prevent horizontal scrolling
  useEffect(() => {
    // Save original overflow-x
    const originalOverflowX = window.document.body.style.overflowX;

    // Set overflow-x: hidden to prevent horizontal scrolling
    window.document.body.style.overflowX = "hidden";

    // Restore original overflow-x when component unmounts
    return () => {
      window.document.body.style.overflowX = originalOverflowX;
    };
  }, []);

  // Use form management hook
  const {
    formData,
    formErrors,
    formSubmitted,
    file,
    fileUploadStatus,
    uploadProgress,
    setFormSubmitted,
    handleChange,
    handleFileUpload,
    handleRemoveFile,
    validateForm,
  } = useMigrationForm();

  // Use migration process hook
  const { migrationResult, toastMessage, showToast, setShowToast, startMigration, setMigrationResult } =
    useMigrationProcess({ accountId: accountId || "" });

  const { generateMigrate } = useMigrateGenerate();

  // Controlamos manualmente el paso activo para incluir Assessment
  const [localActiveStep, setLocalActiveStep] = useState(0);

  // Use file download hook
  const { downloadError, downloadSuccess, handleDownload } = useFileDownload({
    migrationResult,
  });

  let setShowBackConfirmation = false;
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle continue button click
  const handleContinue = async () => {
    if (localActiveStep === 0) {
      setFormSubmitted(true);
      const isValid = validateForm();
      if (isValid && file) {
        setChargingServices(true);
        setLabelCharging(baseSteps[localActiveStep + 1]);
        const responseMigration = await startMigration(formData, file, logout);
        if (!responseMigration) {
          setLocalActiveStep(0);
        } else {
          setLocalActiveStep(1);
        }
        setChargingServices(false);
      } else {
        setLabelToast("Please complete all required fields before continuing.");
        setShowToast(true);
      }
    } else if (localActiveStep === 1) {
      setChargingServices(true);
      setLocalActiveStep(2);
      setLabelCharging(baseSteps[localActiveStep + 1]);
      const appiIds = migrationResult!
        .content!.assessmentResult!.filter(
          (api: { selected: boolean; appId: any }) => api.selected === true && api.appId
        )
        .map((api: { appId: any }) => api.appId);
      const resGenerate = await generateMigrate(migrationResult?.content?.migrationId!, appiIds!, formData, logout);
      if (resGenerate) {
        setMigrate(resGenerate);
        setLocalActiveStep(3);
      } else {
        setLabelToast("Please try again.");
        setShowToast(true);
        setLocalActiveStep(1);
      }
      setChargingServices(false);
    } else if (localActiveStep === 3) {
      handleCancel();
    }
  };

  // Navigation handlers
  const handleCancel = () => {
    navigate("/migrations");
  };

  const handleBackToSetupClick = () => {
    setShowBackConfirmation = true;
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  // Render content based on active step

  const renderStepContent = () => {
    if (chargingServices) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Generating {labelCharging}...</Typography>
        </Box>
      );
    } else if (localActiveStep === 0) {
      // Initial Setup
      return (
        <Paper sx={{ p: 3 }}>
          <SetupForm
            formData={formData}
            formErrors={formErrors}
            formSubmitted={formSubmitted}
            file={file}
            fileUploadStatus={fileUploadStatus}
            uploadProgress={uploadProgress}
            onChange={handleChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={handleRemoveFile}
          />
        </Paper>
      );
    } else if (localActiveStep === 1 && migrationResult) {
      return (
        <AssessmentStep
          migrationId={migrationResult?.content?.migrationId || ""}
          resListAssesmnet={migrationResult}
          setMigrationResult={setMigrationResult}
          setDisableContinue={setDisableContinue}
        />
      );
    } else if (localActiveStep === 3) {
      return (
        <>
          {/* Success message */}
          {downloadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {downloadSuccess}
            </Alert>
          )}

          {/* Download error */}
          {downloadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Download error: {downloadError}
            </Alert>
          )}

          <Summary
            dataResponse={migrateResult}
            onDownload={handleDownload}
            onBackToSetup={handleBackToSetupClick}
            resListAssesmnet={migrationResult}
          />
        </>
      );
    }

    return null;
  };

  // If loading services
  if (accountIdLoading) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <AdminSidebar open={sidebarOpen} onToggle={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#f4f5f5",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading account information...</Typography>
        </Box>
      </Box>
    );
  }

  // If no accountId after trying to load it, show error message
  if (!accountId) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <AdminSidebar open={sidebarOpen} onToggle={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#f4f5f5",
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ mb: 2, width: "100%", maxWidth: 500 }}>
            Account ID not found. Please log in again.
          </Alert>
          <Button variant="contained" onClick={() => navigate("/login")}>
            Go to login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Admin Sidebar */}
      <AdminSidebar open={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main content */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f4f5f5",
        }}
      >
        <Collapse in={showToast}>
          <Alert
            severity="error"
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={handleCloseToast}>
                <Close fontSize="inherit" />
              </IconButton>
            }
            sx={{
              borderRadius: 0,
              width: "100%",
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 9999,
              boxShadow: 3,
            }}
          >
            {/* {toastMessage || "Please complete all required fields before continuing."} */}
            {toastMessage || labelToast}
          </Alert>
        </Collapse>

        {/* Todo el contenido principal */}
        <Box
          sx={{
            maxWidth: "90%",
            mx: "auto",
            p: 3,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            mb: 8, // Espacio para el footer fijo
          }}
        >
          {/* Header */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton size="small">
                <ChevronLeft />
              </IconButton>
              <Typography variant="h5" sx={{ color: "#414141" }}>
                Migrate APIs
              </Typography>
            </Box>
          </Paper>

          {/* Stepper */}
          <Paper sx={{ p: 3 }}>
            <Stepper
              activeStep={localActiveStep}
              sx={{
                "& .MuiStepIcon-root": {
                  color: "#e8e8e8",
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "#478b1a", // Siempre usar el color verde para el paso activo
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "#478b1a",
                },
                "& .MuiStepConnector-line": {
                  borderColor: "#e8e8e8",
                },
                "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
                  borderColor: "#478b1a", // Siempre usar el color verde para la línea activa
                },
                "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                  borderColor: "#478b1a",
                },
              }}
            >
              {baseSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          {/* Contenido del paso actual */}
          {renderStepContent()}
        </Box>

        {/* Footer (fijo) */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #e0e0e0",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              maxWidth: "90%",
              mx: "auto",
              p: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button variant="outlined" onClick={handleCancel} disabled={localActiveStep === 3}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinue}
              disabled={localActiveStep === 2 || chargingServices || disableContinue} // Deshabilitar durante migración, cuando no se ha seleccionado ningun api y en el último paso
            >
              {localActiveStep !== 3 ? "Continue" : "Back List Migrations"}
              {/* Continue */}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MigrateAPIs;
